import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import useClasses from "hooks/useClasses";
import { useTheme } from "@mui/material/styles";

// MUI
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import MapIcon from "@mui/icons-material/Map";
import NearMeIcon from "@mui/icons-material/NearMe";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CircularProgress from "@mui/material/CircularProgress";

// Store Actions
import {
  getMapData,
  getMapBranchDetail,
  setMapPeriod,
} from "store/actions/analytics";
import { getCompanyAllBranches } from "store/actions/companyAdminUsers";
import { getBranches } from "store/actions/companyAdmin";
import { getMapStyles } from "variables/mapStyles";

import styles from "./mapStyle";

// ── Helpers ──
function nFmt(v) {
  if (v == null) return "—";
  if (typeof v === "object") {
    if (v.available === false) return "N/A";
    if (v.value != null) return v.value.toLocaleString();
    return "—";
  }
  if (typeof v === "number") {
    if (Math.abs(v) >= 1e3) return (v / 1e3).toFixed(1) + "k";
    return v.toLocaleString();
  }
  return String(v);
}

function formatMetricValue(val, suffix = "") {
  if (val === null || val === undefined) return "—";
  if (typeof val === "object") {
    if (val.available === false) return val.reason || "N/A";
    if (val.value != null) return `${val.value}${suffix}`;
    return "—";
  }
  return `${val}${suffix}`;
}

// ── Calculate distance between two coordinates in kilometers (Haversine formula) ──
function getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
  if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return null;
  const R = 6371; // Radius of Earth in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function formatDistance(distKm) {
  if (distKm == null || isNaN(distKm)) return null;
  if (distKm < 1) {
    const meters = Math.round(distKm * 1000);
    return `${meters} m away`;
  }
  return `${distKm.toFixed(1)} km away`;
}

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7 Days" },
  { key: "1m", label: "1 Month" },
  { key: "6m", label: "6 Months" },
  { key: "all", label: "All" },
];

const STATUS_META = {
  active: { label: "Active", color: "#3b82f6", dotColor: "#3b82f6", badgeCls: "badgeActive", desc: "Open & active in last 28 days" },
  partially_active: { label: "Partially Active", color: "#f59e0b", dotColor: "#f59e0b", badgeCls: "badgePartial", desc: "Open, no collection in 28d, active within 90d" },
  partial: { label: "Partially Active", color: "#f59e0b", dotColor: "#f59e0b", badgeCls: "badgePartial", desc: "Open, no collection in 28d, active within 90d" },
  inactive: { label: "Inactive", color: "#6b7280", dotColor: "#6b7280", badgeCls: "badgeInactive", desc: "Closed or no collection in 90+ days" },
};

const LEGEND_STATUSES = [
  { key: "active", label: "Active", color: "#3b82f6" },
  { key: "partially_active", label: "Partially Active", color: "#f59e0b" },
  { key: "inactive", label: "Inactive", color: "#6b7280" },
];

const SPARK_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];

// Default center: Cairo, Egypt
const DEFAULT_CENTER = { lat: 30.0444, lng: 31.2357 };
const DEFAULT_ZOOM = 11;

// ── Derive branch status ──
function getBranchStatus(branch) {
  if (!branch) return "active";
  if (
    branch.is_closed === 1 ||
    branch.is_closed === true ||
    branch.is_closed === "1" ||
    branch.closed === 1 ||
    branch.closed === true ||
    branch.closed === "1" ||
    branch.is_active === 0 ||
    branch.is_active === false ||
    branch.is_active === "0"
  ) {
    return "inactive";
  }
  const s = String(
    branch.status ||
    branch.activity_status ||
    branch.operational_status ||
    ""
  ).toLowerCase().trim();

  if (s === "active" || s === "open" || s === "enabled") return "active";
  if (s === "partially_active" || s === "partial" || s === "partially active" || s === "partially-active") return "partially_active";
  if (s === "inactive" || s === "closed" || s === "disabled") return "inactive";
  return "active";
}

// ── Google Maps Colored Pin Creators ──
function createAdvancedPinElement(color, isSelected = false) {
  if (window.google?.maps?.marker?.PinElement) {
    try {
      const pin = new window.google.maps.marker.PinElement({
        background: color,
        borderColor: "#ffffff",
        glyphColor: "#ffffff",
        scale: isSelected ? 1.25 : 1.0,
      });
      return pin.element;
    } catch (e) {
      console.warn("PinElement instantiation error:", e);
    }
  }

  // Fallback custom DOM pin for AdvancedMarkerElement
  const pinDiv = document.createElement("div");
  pinDiv.style.cursor = "pointer";
  pinDiv.style.transform = isSelected ? "scale(1.25)" : "scale(1)";
  pinDiv.style.transformOrigin = "bottom center";
  pinDiv.style.transition = "transform 0.2s ease";
  pinDiv.innerHTML = `
    <svg width="30" height="40" viewBox="0 0 30 40" fill="none" xmlns="http://www.w3.org/2000/svg" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.35));">
      <path d="M15 0C6.71573 0 0 6.71573 0 15C0 24.5 15 40 15 40C15 40 30 24.5 30 15C30 6.71573 23.2843 0 15 0Z" fill="${color}" stroke="#ffffff" stroke-width="1.8"/>
      <circle cx="15" cy="14" r="5.5" fill="#ffffff"/>
      <circle cx="15" cy="14" r="3" fill="${color}"/>
    </svg>
  `;
  return pinDiv;
}

function createMarkerSvgIcon(color, isSelected = false) {
  const width = isSelected ? 36 : 30;
  const height = isSelected ? 48 : 40;
  const anchorX = width / 2;
  const anchorY = height;

  const svg = `<svg width="30" height="40" viewBox="0 0 30 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 0C6.71573 0 0 6.71573 0 15C0 24.5 15 40 15 40C15 40 30 24.5 30 15C30 6.71573 23.2843 0 15 0Z" fill="${color}" stroke="#ffffff" stroke-width="2"/>
    <circle cx="15" cy="14" r="5.5" fill="#ffffff"/>
    <circle cx="15" cy="14" r="3" fill="${color}"/>
  </svg>`;

  return {
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(width, height),
    origin: new window.google.maps.Point(0, 0),
    anchor: new window.google.maps.Point(anchorX, anchorY),
  };
}

// ── Extract branch list from various possible API response structures ──
function extractBranchesFromMapData(mapData) {
  if (!mapData) return [];
  if (Array.isArray(mapData)) return mapData;
  if (Array.isArray(mapData.data)) return mapData.data;
  if (Array.isArray(mapData.branches)) return mapData.branches;
  if (mapData.data && Array.isArray(mapData.data.branches)) return mapData.data.branches;
  if (mapData.data && Array.isArray(mapData.data.data)) return mapData.data.data;
  return [];
}

// ── Robust Coordinate Extractor ──
function extractCoords(b) {
  if (!b || typeof b !== "object") {
    return { lat: null, lng: null, rawLat: undefined, rawLng: undefined };
  }

  // Check all possible property names for latitude and longitude
  const rawLat =
    b.lat ??
    b.latitude ??
    b.latProps ??
    b.location?.lat ??
    b.location?.latitude ??
    b.coordinates?.lat ??
    b.coordinates?.latitude ??
    (Array.isArray(b.coordinates) && b.coordinates.length >= 2 ? b.coordinates[0] : undefined) ??
    b.branch_lat ??
    b.branch?.lat ??
    b.branch?.latitude ??
    b.map?.lat ??
    b.geo?.lat;

  const rawLng =
    b.lng ??
    b.longitude ??
    b.lngProps ??
    b.location?.lng ??
    b.location?.longitude ??
    b.coordinates?.lng ??
    b.coordinates?.longitude ??
    (Array.isArray(b.coordinates) && b.coordinates.length >= 2 ? b.coordinates[1] : undefined) ??
    b.branch_lng ??
    b.branch?.lng ??
    b.branch?.longitude ??
    b.map?.lng ??
    b.geo?.lng;

  const parsedLat = rawLat !== undefined && rawLat !== null && rawLat !== "" ? parseFloat(rawLat) : NaN;
  const parsedLng = rawLng !== undefined && rawLng !== null && rawLng !== "" ? parseFloat(rawLng) : NaN;

  return {
    rawLat,
    rawLng,
    lat: !isNaN(parsedLat) ? parsedLat : null,
    lng: !isNaN(parsedLng) ? parsedLng : null,
  };
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  Main Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export default function MapPage() {
  const classes = useClasses(styles);
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Redux state
  const { allCompanyBranches, branches: companyBranches } = useSelector((s) => s.companyAdmin);
  const { branches: globalBranches } = useSelector((s) => s.branches || {});
  const { mapData, branchDetail, mapPeriod, mapLoading } = useSelector((s) => s.analytics);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);

  // User location state
  const [userLocation, setUserLocation] = useState(null);
  const [locating, setLocating] = useState(false);

  // Google Map refs
  const mapContainerRef = useRef(null);
  const googleMapRef = useRef(null);
  const markersRef = useRef([]);
  const userMarkerRef = useRef(null);
  const userCircleRef = useRef(null);

  // ── Fetch user current location ──
  const fetchUserLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        console.log("📍 [MapPage] User current location retrieved:", coords);
        setUserLocation(coords);
        setLocating(false);
      },
      (err) => {
        console.warn("Geolocation warning:", err);
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, []);

  // Get user location on mount
  useEffect(() => {
    fetchUserLocation();
  }, [fetchUserLocation]);

  // ── Fetch Analytics Map Data whenever period, status, or search changes ──
  const fetchMapAnalytics = useCallback(() => {
    const apiStatus = statusFilter === "partial" ? "partially_active" : statusFilter;
    const params = {
      period: mapPeriod || "1m",
      status: apiStatus || "all",
    };
    if (search && search.trim()) {
      params.search = search.trim();
    }
    console.log("📡 [MapPage] Dispatching getMapData with params:", params);
    dispatch(getMapData(params, history));
  }, [dispatch, history, mapPeriod, statusFilter, search]);

  useEffect(() => {
    fetchMapAnalytics();
  }, [fetchMapAnalytics]);

  // ── Fetch fallback branch lists on mount ──
  useEffect(() => {
    dispatch(getCompanyAllBranches(history));
    dispatch(getBranches({ page: 1, amount: 100 }, history));
  }, [dispatch, history]);

  // Mark initial loading done when any branch source arrives
  useEffect(() => {
    if (mapData || allCompanyBranches || companyBranches || globalBranches) {
      setLoading(false);
    }
  }, [mapData, allCompanyBranches, companyBranches, globalBranches]);

  const handlePeriod = (key) => {
    dispatch(setMapPeriod(key));
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchMapAnalytics();
    dispatch(getCompanyAllBranches(history));
    dispatch(getBranches({ page: 1, amount: 100 }, history));
  };

  const handleLocateMe = () => {
    if (userLocation && googleMapRef.current) {
      googleMapRef.current.panTo(userLocation);
      googleMapRef.current.setZoom(15);
    } else {
      fetchUserLocation();
    }
  };

  // Derive unified branches array (merging analytics mapData with branch store by ID)
  const branches = useMemo(() => {
    console.log("🔍 [MapPage] REDUX STORE RAW DATA SOURCES:", {
      "state.analytics.mapData (getMapData)": mapData,
      "state.companyAdmin.branches (getBranches)": companyBranches,
      "state.companyAdmin.allCompanyBranches (getCompanyAllBranches)": allCompanyBranches,
      "state.branches.branches": globalBranches,
    });

    const fromMapData = extractBranchesFromMapData(mapData);
    const toArray = (src) => {
      if (!src) return [];
      if (Array.isArray(src)) return src;
      if (Array.isArray(src.data)) return src.data;
      if (src.data && Array.isArray(src.data.data)) return src.data.data;
      if (Array.isArray(src.branches)) return src.branches;
      return [];
    };

    const fromCompanyBranches = toArray(companyBranches);
    const fromAllCompanyBranches = toArray(allCompanyBranches);
    const fromGlobalBranches = toArray(globalBranches);

    console.log("🔍 [MapPage] Extracted candidate branch lists:", {
      fromMapDataLength: fromMapData.length,
      fromCompanyBranchesLength: fromCompanyBranches.length,
      fromAllCompanyBranchesLength: fromAllCompanyBranches.length,
      fromGlobalBranchesLength: fromGlobalBranches.length,
    });

    // Merge by ID giving precedence to analytics mapData, but preserving valid properties (like coords) from any source
    const mapById = new Map();
    [...fromAllCompanyBranches, ...fromGlobalBranches, ...fromCompanyBranches, ...fromMapData].forEach((b) => {
      if (!b) return;
      const key = b.id || b._id || b.name || Math.random();
      const existing = mapById.get(key) || {};

      // Filter out null / undefined / empty string values so they do not overwrite existing valid values
      const cleanB = {};
      Object.entries(b).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== "") {
          cleanB[k] = v;
        }
      });

      // Ensure if existing record had valid coordinates, we preserve them if new incoming source lacked coords
      const existingCoords = extractCoords(existing);
      const newCoords = extractCoords(cleanB);

      const merged = { ...existing, ...cleanB };
      if (newCoords.lat === null && existingCoords.lat !== null) {
        merged.lat = existingCoords.lat;
      }
      if (newCoords.lng === null && existingCoords.lng !== null) {
        merged.lng = existingCoords.lng;
      }

      mapById.set(key, merged);
    });

    const mergedList = Array.from(mapById.values());

    console.log("🗺️ [MapPage] Final combined branches count:", mergedList.length);
    mergedList.forEach((b, idx) => {
      const coords = extractCoords(b);
      console.log(`📌 [Branch #${idx + 1}] ID: ${b.id}, Name: "${b.name}"`, {
        rawLat: coords.rawLat,
        rawLng: coords.rawLng,
        parsedLat: coords.lat,
        parsedLng: coords.lng,
        hasValidCoords: coords.lat !== null && coords.lng !== null,
        fullObject: b,
      });
    });

    return mergedList;
  }, [mapData, allCompanyBranches, companyBranches, globalBranches]);

  // Compute status counters
  const counters = useMemo(() => {
    if (mapData && typeof mapData === "object" && mapData.counts) {
      return {
        active: mapData.counts.active || 0,
        partially_active: mapData.counts.partially_active || mapData.counts.partial || 0,
        inactive: mapData.counts.inactive || 0,
      };
    }
    const c = { active: 0, partially_active: 0, inactive: 0 };
    branches.forEach((b) => {
      const s = getBranchStatus(b);
      if (c[s] !== undefined) c[s]++;
      else if (s === "partial") c.partially_active++;
      else c.active++;
    });
    return c;
  }, [mapData, branches]);

  // Filter and sort branches (sorted by nearest distance if userLocation is known)
  const filteredBranches = useMemo(() => {
    let list = branches;
    if (search && search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (b) =>
          (b.name && b.name.toLowerCase().includes(q)) ||
          (b.name_ar && b.name_ar.toLowerCase().includes(q)) ||
          (b.address && b.address.toLowerCase().includes(q)) ||
          (b.address_ar && b.address_ar.toLowerCase().includes(q))
      );
    }
    if (statusFilter !== "all") {
      const normalizedFilter = statusFilter === "partial" ? "partially_active" : statusFilter;
      list = list.filter((b) => getBranchStatus(b) === normalizedFilter);
    }

    if (userLocation) {
      list = [...list].sort((a, b) => {
        const cA = extractCoords(a);
        const cB = extractCoords(b);
        const distA =
          cA.lat !== null && cA.lng !== null
            ? getDistanceFromLatLonInKm(userLocation.lat, userLocation.lng, cA.lat, cA.lng)
            : Infinity;
        const distB =
          cB.lat !== null && cB.lng !== null
            ? getDistanceFromLatLonInKm(userLocation.lat, userLocation.lng, cB.lat, cB.lng)
            : Infinity;
        return distA - distB;
      });
    }

    return list;
  }, [branches, search, statusFilter, userLocation]);

  // ── Click pin → show detail on right ──
  const handlePinClick = useCallback((branchId) => {
    if (branchId) {
      dispatch(getMapBranchDetail(branchId, { period: mapPeriod }, history));
      setSelectedBranchId(branchId);
    }
  }, [dispatch, history, mapPeriod]);

  // ── Click branch item → center map on that branch ──
  const handleNearbyClick = useCallback((branch) => {
    const coords = extractCoords(branch);
    const map = googleMapRef.current;

    console.log(`🎯 [MapPage] Clicked branch "${branch.name}":`, {
      rawLat: coords.rawLat,
      rawLng: coords.rawLng,
      lat: coords.lat,
      lng: coords.lng,
      branch,
    });

    if (map && coords.lat !== null && coords.lng !== null) {
      const pos = new window.google.maps.LatLng(coords.lat, coords.lng);
      map.panTo(pos);
      map.setZoom(16);
    } else {
      console.warn(`⚠️ [MapPage] Cannot pan to branch "${branch.name}" - Missing valid coordinates:`, coords);
    }

    // Also fetch detail and select
    if (branch.id) {
      dispatch(getMapBranchDetail(branch.id, { period: mapPeriod }, history));
      setSelectedBranchId(branch.id);
    }
  }, [dispatch, history, mapPeriod]);

  // ── Re-fetch branch detail whenever period changes and a branch is open ──
  useEffect(() => {
    if (selectedBranchId) {
      dispatch(getMapBranchDetail(selectedBranchId, { period: mapPeriod }, history));
    }
  }, [dispatch, history, selectedBranchId, mapPeriod]);

  const closeDetail = () => {
    setSelectedBranchId(null);
  };

  // ── Initialize Google Map (with polling for deferred script & container ready) ──
  const initMap = useCallback(() => {
    const google = window.google;
    if (!google || !mapContainerRef.current || googleMapRef.current) return;

    try {
      const map = new google.maps.Map(mapContainerRef.current, {
        zoom: DEFAULT_ZOOM,
        center: DEFAULT_CENTER,
        styles: getMapStyles(isDark),
        disableDefaultUI: false,
        zoomControl: true,
        fullscreenControl: true,
        streetViewControl: false,
        mapTypeControl: false,
        gestureHandling: "greedy",
        mapId: process.env.REACT_APP_GOOGLE_MAP_API_ID,
      });

      googleMapRef.current = map;
      setMapLoaded(true);
    } catch (err) {
      console.error("Map init error:", err);
    }
  }, [isDark]);

  useEffect(() => {
    if (window.google && mapContainerRef.current) {
      initMap();
    } else {
      const timer = setInterval(() => {
        if (window.google && mapContainerRef.current) {
          initMap();
          clearInterval(timer);
        }
      }, 100);
      return () => clearInterval(timer);
    }
  }, [initMap]);

  // Dynamic theme update for Google Map tiles when dark/light mode toggles
  useEffect(() => {
    if (googleMapRef.current) {
      googleMapRef.current.setOptions({ styles: getMapStyles(isDark) });
    }
  }, [isDark]);

  // ── Render User Location Marker & Circle ──
  useEffect(() => {
    const google = window.google;
    const map = googleMapRef.current;
    if (!google || !map) return;

    if (userMarkerRef.current) {
      userMarkerRef.current.setMap(null);
      userMarkerRef.current = null;
    }
    if (userCircleRef.current) {
      userCircleRef.current.setMap(null);
      userCircleRef.current = null;
    }

    if (userLocation) {
      // Outer subtle accuracy halo
      userCircleRef.current = new google.maps.Circle({
        strokeColor: "#2563eb",
        strokeOpacity: 0.4,
        strokeWeight: 1.5,
        fillColor: "#3b82f6",
        fillOpacity: 0.15,
        map,
        center: userLocation,
        radius: 300,
      });

      // User location marker
      userMarkerRef.current = new google.maps.Marker({
        position: userLocation,
        map,
        title: "Your Current Location",
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 9,
          fillColor: "#2563eb",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 3,
        },
        zIndex: 1000,
      });

      const infoWindow = new google.maps.InfoWindow({
        content: `<div style="padding:6px 10px;font-weight:600;font-size:12px;color:#1e293b;font-family:sans-serif;">📍 Your Location</div>`,
      });

      userMarkerRef.current.addListener("click", () => {
        infoWindow.open(map, userMarkerRef.current);
      });
    }
  }, [userLocation, mapLoaded]);

  // ── Place markers when filteredBranches, selectedBranchId, or mapLoaded change ──
  useEffect(() => {
    const google = window.google;
    const map = googleMapRef.current;
    if (!google || !map) return;

    // Clear existing markers
    markersRef.current.forEach((m) => {
      if (m.setMap) m.setMap(null);
      if (m.map !== undefined) m.map = null;
    });
    markersRef.current = [];

    console.log("🗺️ [MapPage] Plotting markers for filtered branches count:", filteredBranches.length);

    if (filteredBranches.length === 0 && !userLocation) return;

    const bounds = new google.maps.LatLngBounds();
    let hasValidCoords = false;

    // Normal Google Map pins for each branch (matching standard Google Map marker)
    filteredBranches.forEach((b, idx) => {
      const coords = extractCoords(b);

      console.log(`📌 [Google Maps Marker #${idx + 1}] Branch "${b.name}" (ID: ${b.id}):`, {
        rawLat: coords.rawLat,
        rawLng: coords.rawLng,
        parsedLat: coords.lat,
        parsedLng: coords.lng,
        isValid: coords.lat !== null && coords.lng !== null,
        fullObject: b,
      });

      if (coords.lat === null || coords.lng === null) {
        console.warn(
          `⚠️ [Google Maps Marker] Skipping branch "${b.name}" (ID: ${b.id}) - No valid lat/lng coordinates found.`,
          { rawLat: coords.rawLat, rawLng: coords.rawLng, branch: b }
        );
        return;
      }

      hasValidCoords = true;
      const position = new google.maps.LatLng(coords.lat, coords.lng);
      bounds.extend(position);

      const branchStatus = getBranchStatus(b);
      const statusMeta = STATUS_META[branchStatus] || STATUS_META.active;
      const isSelected = selectedBranchId === b.id;
      const pinColor = statusMeta.color;

      let marker = null;
      if (google.maps.marker && google.maps.marker.AdvancedMarkerElement) {
        try {
          const pinContent = createAdvancedPinElement(pinColor, isSelected);
          marker = new google.maps.marker.AdvancedMarkerElement({
            position,
            map,
            title: b.name || "Branch",
            content: pinContent,
            zIndex: isSelected ? 1000 : (branchStatus === "active" ? 20 : branchStatus === "partially_active" ? 10 : 1),
          });
        } catch (err) {
          console.warn("AdvancedMarkerElement init failed, falling back to standard Marker:", err);
        }
      }

      // Fallback or standard Marker
      if (!marker) {
        marker = new google.maps.Marker({
          position,
          map,
          title: b.name || "Branch",
          icon: createMarkerSvgIcon(pinColor, isSelected),
          zIndex: isSelected ? 1000 : (branchStatus === "active" ? 20 : branchStatus === "partially_active" ? 10 : 1),
        });
      }

      const distKm =
        userLocation && coords.lat !== null && coords.lng !== null
          ? getDistanceFromLatLonInKm(userLocation.lat, userLocation.lng, coords.lat, coords.lng)
          : null;
      const distanceLabel =
        distKm !== null
          ? `<div style="font-size:11px;font-weight:600;color:#2563eb;margin-top:4px;">📍 ${formatDistance(distKm)}</div>`
          : `<div style="font-size:10px;color:#2563eb;margin-top:4px;font-family:monospace;">Lat: ${coords.lat.toFixed(5)}, Lng: ${coords.lng.toFixed(5)}</div>`;

      const infoWindow = new google.maps.InfoWindow({
        content: `<div style="padding:8px 12px;font-family:sans-serif;min-width:180px;">
          <div style="font-weight:700;font-size:13px;color:#1e293b;">${b.name || "Branch"}</div>
          ${b.name_ar ? `<div style="font-size:11px;color:#64748b;direction:rtl;margin-top:2px;">${b.name_ar}</div>` : ""}
          <div style="font-size:11px;color:#64748b;margin-top:4px;">${b.address || "Branch Location"}</div>
          <div style="display:inline-block;padding:2px 8px;border-radius:4px;font-size:10px;font-weight:600;margin-top:6px;background:${statusMeta.color}15;color:${statusMeta.color};">
            ${statusMeta.label}
          </div>
          ${distanceLabel}
        </div>`,
      });

      marker.addListener("click", () => {
        handlePinClick(b.id);
        try {
          infoWindow.open({
            anchor: marker,
            map,
          });
        } catch (e) {
          infoWindow.open(map, marker);
        }
      });

      markersRef.current.push(marker);
    });

    if (userLocation) {
      bounds.extend(userLocation);
      hasValidCoords = true;
    }

    // Fit map bounds on load (when not explicitly selecting a branch)
    if (hasValidCoords && !selectedBranchId) {
      map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
      const listener = google.maps.event.addListener(map, "idle", () => {
        if (map.getZoom() > 16) map.setZoom(16);
        google.maps.event.removeListener(listener);
      });
    }
  }, [filteredBranches, selectedBranchId, userLocation, mapLoaded, handlePinClick]);

  const isDataLoading = mapLoading || (loading && branches.length === 0);

  // ─── Render ────────────────────────────────
  return (
    <div className={classes.pageWrapper}>
      {/* ── Control Bar ── */}
      <div className={classes.controlBar}>
        <div className={classes.periodPills}>
          {PERIODS.map((p) => (
            <button
              key={p.key}
              className={`${classes.pill} ${mapPeriod === p.key ? classes.pillActive : ""}`}
              onClick={() => handlePeriod(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button className={classes.refreshBtn} onClick={handleLocateMe} disabled={locating}>
            <MyLocationIcon style={{ fontSize: 16 }} />
            {locating ? "Locating…" : "My Location"}
          </button>
          <button className={classes.refreshBtn} onClick={handleRefresh} disabled={isDataLoading}>
            <RefreshIcon style={{ fontSize: 16 }} />
            {isDataLoading ? "Loading…" : "Refresh Data"}
          </button>
        </div>
      </div>

      {/* ── Search & Filters ── */}
      <div className={classes.filtersBar}>
        <div className={classes.searchWrapper}>
          <SearchIcon className={classes.searchIcon} />
          <input
            className={classes.searchInput}
            placeholder="Search branch name or address..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className={classes.filterSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="partially_active">Partially Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* ── Main Layout: Map + Detail Sidebar ── */}
      <div className={selectedBranchId ? classes.mapLayoutWithDetail : classes.mapLayoutFull}>

        {/* ── Map Container ── */}
        <div className={classes.mapContainer}>
          <div ref={mapContainerRef} className={classes.mapCanvas} />

          {isDataLoading && (
            <div
              className={classes.loadingOverlay}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 50,
                background: isDark ? "rgba(15,17,23,0.85)" : "rgba(255,255,255,0.85)",
                backdropFilter: "blur(4px)",
              }}
            >
              <CircularProgress size={32} sx={{ color: "#3b82f6", mr: 1.5 }} />
              Loading analytics map data…
            </div>
          )}

          {filteredBranches.length === 0 && !isDataLoading && (
            <div className={classes.mapPlaceholder}>
              <MapIcon />
              <div>No branches match filters</div>
            </div>
          )}

          {/* Status Legend — top left on map */}
          <div className={classes.statusLegend}>
            <div className={classes.statusLegendTitle}>Branch Status</div>
            {LEGEND_STATUSES.map((item) => (
              <div key={item.key} className={classes.statusLegendRow}>
                <span className={classes.statusLegendLabel}>
                  <span className={classes.statusDot} style={{ background: item.color }} />
                  {item.label}
                </span>
                <span className={classes.statusLegendCount}>
                  {counters[item.key] ?? 0}
                </span>
              </div>
            ))}
            <div className={classes.statusLegendRow} style={{ marginTop: 4, paddingTop: 6, borderTop: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid #f3f4f6" }}>
              <span className={classes.statusLegendLabel}>
                <span className={classes.statusDot} style={{ background: "#2563eb" }} />
                Your Location
              </span>
              <span className={classes.statusLegendCount} style={{ fontSize: 12, color: userLocation ? "#10b981" : "#9ca3af" }}>
                {userLocation ? "Active" : locating ? "Seeking…" : "Off"}
              </span>
            </div>
          </div>

          {/* ── All Branches List — bottom left overlay on map ── */}
          <div className={classes.nearbyOverlay}>
            <div className={classes.nearbyHeader}>
              <NearMeIcon style={{ fontSize: 18, color: "#60a5fa" }} />
              All Branches List {userLocation ? "(Sorted by Nearest)" : ""}
            </div>
            <div className={classes.nearbyList}>
              {filteredBranches.length === 0 && (
                <div style={{ padding: "14px 18px", color: isDark ? "rgba(255,255,255,0.3)" : "#9ca3af", fontSize: 13 }}>
                  No branches found
                </div>
              )}
              {filteredBranches.map((b, i) => {
                const meta = STATUS_META[getBranchStatus(b)] || STATUS_META.active;
                const coords = extractCoords(b);
                const hasCoords = coords.lat !== null && coords.lng !== null;
                const distKm =
                  userLocation && hasCoords
                    ? getDistanceFromLatLonInKm(userLocation.lat, userLocation.lng, coords.lat, coords.lng)
                    : null;

                return (
                  <div
                    key={b.id || i}
                    className={`${classes.nearbyItem} ${selectedBranchId === b.id ? classes.nearbyItemActive : ""}`}
                    onClick={() => handleNearbyClick(b)}
                  >
                    <div>
                      <div className={classes.nearbyName}>{b.name || `Branch ${i + 1}`}</div>
                      <div className={hasCoords ? classes.nearbyCoords : classes.nearbyCoordsMissing}>
                        {hasCoords
                          ? distKm !== null
                            ? `📍 ${formatDistance(distKm)}`
                            : `📍 Lat: ${coords.lat.toFixed(4)}, Lng: ${coords.lng.toFixed(4)}`
                          : "⚠️ No Lat/Lng configured"}
                      </div>
                      {b.address && (
                        <div className={classes.nearbyAddress}>
                          {b.address}
                        </div>
                      )}
                    </div>
                    <span className={`${classes.nearbyBadge} ${classes[meta.badgeCls]}`}>
                      {meta.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Branch Detail Panel (right side, only when a branch is selected) ── */}
        {selectedBranchId && (
          <div className={classes.detailPanel}>
            <button className={classes.detailClose} onClick={closeDetail}>
              <CloseIcon style={{ fontSize: 18 }} />
            </button>
            <BranchDetailContent
              classes={classes}
              detail={
                branchDetail && (branchDetail.id === selectedBranchId || !branchDetail.id)
                  ? { ...(branches.find((b) => b.id === selectedBranchId) || {}), ...branchDetail }
                  : branches.find((b) => b.id === selectedBranchId) || branchDetail
              }
              history={history}
              isDark={isDark}
              userLocation={userLocation}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Branch Detail Content (shown in right panel) ──────────────────
function BranchDetailContent({ classes, detail, history, isDark, userLocation }) {
  const d = detail || {};
  const statusMeta = STATUS_META[getBranchStatus(d)] || STATUS_META.active;
  const detailCoords = extractCoords(d);
  const detailDistKm =
    userLocation && detailCoords.lat !== null && detailCoords.lng !== null
      ? getDistanceFromLatLonInKm(userLocation.lat, userLocation.lng, detailCoords.lat, detailCoords.lng)
      : null;

  // Request Activity Counts
  const assignedCount =
    d.assigned_requests ??
    d.assigned_requests_count ??
    d.requests?.assigned ??
    d.assigned_count ??
    d.assigned ??
    0;
  const pendingCount =
    d.pending_requests ??
    d.pending_requests_count ??
    d.requests?.pending ??
    d.pending_count ??
    d.pending ??
    0;
  const completedCount =
    d.completed_requests ??
    d.completed_requests_count ??
    d.completed_dropoffs ??
    d.requests?.completed ??
    d.completed_count ??
    d.completed ??
    0;
  const archivedCount =
    d.archived_requests ??
    d.archived_requests_count ??
    d.requests?.archived ??
    d.archived_count ??
    d.archived ??
    0;

  // Collected metrics
  const deliveredItems =
    d.collected_units ??
    d.collected_items_count ??
    d.delivered_items ??
    d.collected_items ??
    d.total_collected;
  const collectedWeight = d.collected_weight_kg ?? d.collected_weight;
  const avgMonthly =
    d.average_monthly_collected_items ??
    d.avg_monthly_collected_items ??
    d.avg_monthly_collected ??
    d.avg_monthly_collection;

  // Sparkline data (6 months)
  const sparkRaw =
    d.last_six_months ||
    d.collection_trend ||
    d.collection_history ||
    d.sparkline ||
    d.daily_activity ||
    d.trend ||
    [];

  const sparkData = useMemo(() => {
    if (!Array.isArray(sparkRaw) || sparkRaw.length === 0) {
      return SPARK_MONTHS.map((m) => ({ label: m, value: 0 }));
    }
    return sparkRaw.slice(-6).map((item, idx) => {
      if (typeof item === "number") {
        return {
          label: SPARK_MONTHS[idx % SPARK_MONTHS.length],
          value: item,
        };
      }
      if (item && typeof item === "object") {
        let label = SPARK_MONTHS[idx % SPARK_MONTHS.length];
        if (item.date) {
          try {
            const dateObj = new Date(item.date);
            if (!isNaN(dateObj.getTime())) {
              label = dateObj.toLocaleDateString("en-US", { month: "short" });
            }
          } catch (e) {}
        } else if (item.month) {
          label = String(item.month);
        }
        const val =
          item.collected_items ??
          item.completed_dropoffs ??
          item.items ??
          item.dropoffs ??
          item.weight ??
          item.value ??
          0;
        return { label, value: typeof val === "number" ? val : parseFloat(val) || 0 };
      }
      return { label: SPARK_MONTHS[idx % SPARK_MONTHS.length], value: 0 };
    });
  }, [sparkRaw]);

  const sparkMax = Math.max(...sparkData.map((v) => v.value), 1);

  return (
    <>
      {/* Branch icon */}
      {d.icon && (
        <div className={classes.detailAvatar}>
          <img src={d.icon} alt={d.name} />
        </div>
      )}

      <div className={classes.detailHeader}>
        <div className={classes.detailTitle}>{d.name || "Branch"}</div>
        <span
          className={classes.detailStatusBadge}
          style={{
            background: `${statusMeta.color}18`,
            color: statusMeta.color,
          }}
        >
          {statusMeta.label}
        </span>
      </div>

      {d.name_ar && <div className={classes.detailSubtitle}>الاسم بالعربي: {d.name_ar}</div>}

      {/* Info rows */}
      <div className={classes.detailInfoSection}>
        {detailDistKm !== null && (
          <div className={classes.detailInfoRow}>
            <span className={classes.detailInfoLabel}>Distance</span>
            <span className={classes.detailInfoValue} style={{ color: "#3b82f6", fontWeight: 700 }}>
              📍 {formatDistance(detailDistKm)}
            </span>
          </div>
        )}
        {(d.company_name || d.company?.name) && (
          <div className={classes.detailInfoRow}>
            <span className={classes.detailInfoLabel}>Company</span>
            <span className={classes.detailInfoValue}>{d.company_name || d.company?.name}</span>
          </div>
        )}
        {d.address && (
          <div className={classes.detailInfoRow}>
            <span className={classes.detailInfoLabel}>Address</span>
            <span className={classes.detailInfoValue}>{d.address}</span>
          </div>
        )}
        {d.address_ar && (
          <div className={classes.detailInfoRow}>
            <span className={classes.detailInfoLabel}>العنوان</span>
            <span className={classes.detailInfoValue} style={{ direction: "rtl" }}>{d.address_ar}</span>
          </div>
        )}
        {d.phone && (
          <div className={classes.detailInfoRow}>
            <span className={classes.detailInfoLabel}>Phone</span>
            <span className={classes.detailInfoValue}>{d.phone}</span>
          </div>
        )}
        <div className={classes.detailInfoRow}>
          <span className={classes.detailInfoLabel}>Status</span>
          <span className={classes.detailInfoValue} style={{ color: statusMeta.color }}>
            {statusMeta.label}
          </span>
        </div>
        {(d.opening_date || d.registered_at || d.created_at) && (
          <div className={classes.detailInfoRow}>
            <span className={classes.detailInfoLabel}>Registered / Opening</span>
            <span className={classes.detailInfoValue}>{d.opening_date || d.registered_at || d.created_at}</span>
          </div>
        )}
        {(d.last_collection || d.last_collection_at) && (
          <div className={classes.detailInfoRow}>
            <span className={classes.detailInfoLabel}>Last Collection</span>
            <span className={classes.detailInfoValue}>{d.last_collection || d.last_collection_at}</span>
          </div>
        )}
      </div>

      {/* Request Activity Counters */}
      <div style={{ marginBottom: 12 }}>
        <div className={classes.sparkTitle}>Request Activity</div>
        <div className={classes.detailStatsGrid}>
          <div className={classes.detailStatItem}>
            <div className={classes.detailStatLabel}>Assigned</div>
            <div className={classes.detailStatValue} style={{ color: "#3b82f6" }}>{nFmt(assignedCount)}</div>
          </div>
          <div className={classes.detailStatItem}>
            <div className={classes.detailStatLabel}>Pending</div>
            <div className={classes.detailStatValue} style={{ color: "#f59e0b" }}>{nFmt(pendingCount)}</div>
          </div>
          <div className={classes.detailStatItem}>
            <div className={classes.detailStatLabel}>Completed</div>
            <div className={classes.detailStatValue} style={{ color: "#10b981" }}>{nFmt(completedCount)}</div>
          </div>
          <div className={classes.detailStatItem}>
            <div className={classes.detailStatLabel}>Archived</div>
            <div className={classes.detailStatValue} style={{ color: "#6b7280" }}>{nFmt(archivedCount)}</div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className={classes.detailStatsGrid}>
        <div className={classes.detailStatItem}>
          <div className={classes.detailStatLabel}>Collected Weight</div>
          <div className={classes.detailStatValue}>
            {d.collected_weight_kg !== undefined && d.collected_weight_kg !== null
              ? `${nFmt(d.collected_weight_kg)} kg`
              : formatMetricValue(collectedWeight, " tonnes")}
          </div>
        </div>
        <div className={classes.detailStatItem}>
          <div className={classes.detailStatLabel}>Delivered Items</div>
          <div className={classes.detailStatValue}>{nFmt(deliveredItems)}</div>
        </div>
        <div className={classes.detailStatItem} style={{ gridColumn: "span 2" }}>
          <div className={classes.detailStatLabel}>
            {d.average_monthly_collected_items !== undefined || d.avg_monthly_collected_items !== undefined
              ? "Avg Monthly Items"
              : "Avg Monthly Collection"}
          </div>
          <div className={classes.detailStatValue}>
            {d.average_monthly_collected_items !== undefined && d.average_monthly_collected_items !== null
              ? `${nFmt(d.average_monthly_collected_items)} units`
              : d.avg_monthly_collected_items !== undefined && d.avg_monthly_collected_items !== null
              ? `${nFmt(d.avg_monthly_collected_items)} units`
              : formatMetricValue(avgMonthly, " tonnes")}
          </div>
        </div>
      </div>

      {/* Sparkline Chart (6 months) */}
      <div className={classes.detailSparkline}>
        <div className={classes.sparkTitle}>Collection Trend (Last 6 Months)</div>
        <div className={classes.sparkBars}>
          {sparkData.map((item, i) => {
            const valNum = item.value;
            return (
              <div key={i} className={classes.sparkBarCol}>
                <div className={classes.sparkBarValue}>{valNum > 0 ? nFmt(valNum) : ""}</div>
                <div
                  className={classes.sparkBar}
                  style={{
                    height: `${Math.max((valNum / sparkMax) * 100, 6)}%`,
                    background: `linear-gradient(180deg, #3b82f6 0%, #3b82f6${i < 3 ? "66" : "ff"} 100%)`,
                  }}
                />
              </div>
            );
          })}
        </div>
        <div className={classes.sparkLabels}>
          {sparkData.map((item, i) => (
            <span key={i} className={classes.sparkLabel}>{item.label}</span>
          ))}
        </div>
      </div>

      {/* Action Buttons: Open All Collected, Not Collected, Archived */}
      {d.id && (
        <div style={{ marginTop: 16 }}>
          <div className={classes.sparkTitle}>Branch Lists & Requests</div>
          <div className={classes.detailActions}>
            <button
              className={`${classes.detailBtn} ${classes.detailBtnCollected}`}
              onClick={() => history.push(`/admin/company-branches/collected/${d.id}`)}
              title="Open All Collected List"
            >
              <span>Open All Collected List</span>
              <ArrowForwardIcon style={{ fontSize: 16 }} />
            </button>
            <button
              className={`${classes.detailBtn} ${classes.detailBtnNotCollected}`}
              onClick={() => history.push(`/admin/company-branches/not-collected/${d.id}`)}
              title="Open All Not Collected List"
            >
              <span>Open All Not Collected List</span>
              <ArrowForwardIcon style={{ fontSize: 16 }} />
            </button>
            <button
              className={`${classes.detailBtn} ${classes.detailBtnArchived}`}
              onClick={() => history.push(`/admin/company-branches/archived-batches/${d.id}`)}
              title="Open All Archived List"
            >
              <span>Open All Archived List</span>
              <ArrowForwardIcon style={{ fontSize: 16 }} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
